// Utilities
export { cn } from './lib/utils';

// Primitives are available as a separate import: '@hr/ui/primitives'
// import * as Primitives from '@hr/ui/primitives'

// Components
export { LogoMark } from './components/logo-mark';
export { Button, buttonVariants } from './components/button';
export { Input } from './components/input';
export { Label } from './components/label';
export {
  Card,
  CardHeader,
  CardFooter,
  CardTitle,
  CardDescription,
  CardContent,
} from './components/card';
export { Avatar, AvatarImage, AvatarFallback } from './components/avatar';
export { Badge, badgeVariants } from './components/badge';
export { Separator } from './components/separator';
export { Checkbox } from './components/checkbox';
export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
} from './components/select';
export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from './components/dialog';
export {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuCheckboxItem,
  DropdownMenuRadioItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuGroup,
  DropdownMenuPortal,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuRadioGroup,
} from './components/dropdown-menu';
export { Tabs, TabsList, TabsTrigger, TabsContent } from './components/tabs';
export {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from './components/tooltip';
export { Toaster } from './components/toaster';
export { useToast, toast } from './components/use-toast';

// Alert component
export { Alert, AlertTitle, AlertDescription } from './components/alert';

// Popover
export { Popover, PopoverTrigger, PopoverContent } from './components/popover';

// Combobox (searchable dropdown)
export { Combobox } from './components/combobox';
export type { ComboboxOption, ComboboxProps } from './components/combobox';

// Scroll Area
export { ScrollArea, ScrollBar } from './components/scroll-area';

// Switch
export { Switch } from './components/switch';

// Progress
export { Progress } from './components/progress';

// Table
export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption,
} from './components/table';

// Textarea
export { Textarea } from './components/textarea';

// Slider
export { Slider } from './components/slider';

// Icons (re-export commonly used ones)
export {
  Loader2,
  Check,
  X,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  Search,
  Plus,
  Minus,
  Edit,
  Trash2,
  MoreHorizontal,
  MoreVertical,
  Settings,
  User,
  Users,
  Building,
  Building2,
  MapPin,
  Calendar,
  Clock,
  Mail,
  Phone,
  FileText,
  Download,
  Upload,
  Eye,
  EyeOff,
  LogOut,
  Menu,
  Bell,
  BellOff,
  Home,
  LayoutDashboard,
  Briefcase,
  DollarSign,
  BarChart3,
  GraduationCap,
  Award,
  AlertCircle,
  AlertTriangle,
  Info,
  CheckCircle,
  CheckCircle2,
  XCircle,
  Network,
  Maximize2,
  CalendarDays,
  Palmtree,
  Shield,
  ShieldAlert,
  ShieldCheck,
  ShieldOff,
  ShieldX,
  Copy,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  ArrowDown,
  CheckCheck,
  Lock,
  Flag,
  Sun,
  Moon,
  Monitor,
  TrendingUp,
  TrendingDown,
  FileCheck,
  FileSignature,
  FileWarning,
  Filter,
  MessageSquare,
  MessageCircle,
  Send,
  Paperclip,
  Link,
  ExternalLink,
  RefreshCw,
  RotateCcw,
  Save,
  FolderOpen,
  Folder,
  File,
  FilePlus,
  FileX,
  Image,
  Video,
  Play,
  Pause,
  Square,
  Circle,
  Star,
  Heart,
  ThumbsUp,
  ThumbsDown,
  Zap,
  Target,
  Crosshair,
  Compass,
  Map,
  Globe,
  Activity,
  PieChart,
  LineChart,
  Layers,
  Grid,
  Grid3X3,
  List,
  Table as TableIcon,
  Database,
  Server,
  Cloud,
  CloudOff,
  Wifi,
  WifiOff,
  Bluetooth,
  Battery,
  Power,
  Smartphone,
  Tablet,
  Laptop,
  Printer,
  Camera,
  Mic,
  MicOff,
  Volume2,
  VolumeX,
  Headphones,
  HelpCircle,
  Music,
  MonitorPlay,
  Radio,
  Tv,
  Film,
  BookOpen,
  Book,
  Bookmark,
  Tag,
  Tags,
  Hash,
  AtSign,
  Pencil,
  Percent,
  CreditCard,
  ShoppingCart,
  ShoppingBag,
  Gift,
  Package,
  Truck,
  Archive,
  Inbox,
  Trash,
  Undo,
  Redo,
  Scissors,
  Clipboard,
  ClipboardCheck,
  GripVertical,
  History,
  ClipboardList,
  Code,
  Terminal,
  Binary,
  Braces,
  Key,
  KeyRound,
  Unlock,
  UserPlus,
  UserMinus,
  UserCheck,
  UserX,
  Users2,
  Crown,
  Sparkles,
  Wand2,
  Lightbulb,
  Flame,
  Droplet,
  Wind,
  Snowflake,
  TreePine,
  Flower,
  TreePalm,
  Bug,
  Share2,
  Skull,
  Ghost,
  Bot,
  Cpu,
  HardDrive,
  Megaphone,
  MemoryStick,
  CircuitBoard,
  ArrowUpDown,
  Plug,
  Unplug,
  Link2,
  GitBranch,
  Pin,
  PinOff,
  Github,
} from 'lucide-react';
